// @ts-ignore
import type { AccountType, FreightService, FreightServiceAddOn } from '@prisma/client';

// These names are mostly arbitrary, I'm just coming up with them on the fly based on existing use
// Jer is going to kill me. Both Tran and Becker.
export const emailColorMap = {
  black: ['#000000'],
  richBlack: ['#231F20'],
  darkerGray: ['#2e2e32', '#2E2E32'],
  darkGray: ['#333333', '#33373d'],
  infoBackground: ['#518990'],
  gray: ['#555555', '#52555a'],
  lightGray: ['#636363'],
  link: ['#81909D'],
  flightTracking: ['#bebec2'],
  rowBackground: ['#dfecf2'],
  lightBorder: ['#e5dfdf', '#dadada'],
  primary: ['#ed7500'],
  offWhite: ['#f1f6fd'],
  white: ['#ffffff'],
} as const satisfies Record<string, string[]>;

export const baseEmailTheme = Object.keys(emailColorMap).reduce(
  (agg, key) => {
    agg[key] = emailColorMap[key][0];
    return agg;
  },
  {} as { -readonly [id in keyof typeof emailColorMap]: (typeof emailColorMap)[id][0] },
);

type RequiredTenantAssets =
  | 'logo'
  | 'logo-dark'
  | 'logo-dark-tagline'
  | 'logo-light'
  | 'logo-light-tagline'
  | 'twitterUrl'
  | 'facebookUrl'
  | 'linkedinUrl'
  | 'siteUrl';
export const baseEmailAssets = {
  facebook: 'https://raw.githubusercontent.com/pelicargo/assets/master/facebook.png',
  flight: 'https://raw.githubusercontent.com/pelicargo/assets/master/flight.png',
  landing: 'https://raw.githubusercontent.com/pelicargo/assets/master/landing.png',
  linkedin: 'https://raw.githubusercontent.com/pelicargo/assets/master/linkedin.png',
  pivot: 'https://raw.githubusercontent.com/pelicargo/assets/master/pivot.png',
  takeoff: 'https://raw.githubusercontent.com/pelicargo/assets/master/takeoff.png',
  twitter: 'https://raw.githubusercontent.com/pelicargo/assets/master/twitter.png',
  'welcome-quote': 'https://raw.githubusercontent.com/pelicargo/assets/master/welcome-quote.jpg',
  'welcome-save': 'https://raw.githubusercontent.com/pelicargo/assets/master/welcome-save.jpg',
  'welcome-ship': 'https://raw.githubusercontent.com/pelicargo/assets/master/welcome-ship.jpg',
} as const;

export type RequestDrawers =
  | 'ProviderQuoting'
  | 'RequestClarifications'
  | 'QuoteUpdate'
  | 'AirlineBooking'
  | 'BookingConfirmation'
  | 'BookingChange'
  | 'BookingCancel'
  | 'NewRate'
  | 'Contact'
  | 'RequestSchedule'
  | 'GenericEmailToFF'
  | 'StatusUpdate';

export type TenantInfo<ID extends string> = {
  id: ID;
  name: string;
  domains: string[];
  // For us - no restrictions
  isSystemTenant: boolean;
  customerTheme: {
    colors: Record<string, Record<string, string>>;
    logoDims: { width: number; height: number };
    fullLogoDims: {
      height: number | string;
      width: number | string;
    };
  };
  emailTheme: Record<keyof typeof emailColorMap, string>;
  emailAssets: Record<keyof typeof baseEmailAssets | RequiredTenantAssets, string>;
  accountTypes: {
    [key in AccountType]: boolean;
  };
  admin: {
    cruds: {
      accounts: boolean;
      providers: boolean;
      airports: boolean;
      contacts: boolean;
      instructions: boolean;
      users: boolean;
      backlogs: boolean;
      surcharges: boolean;
      rates: boolean;
      'route-editor': boolean;
      configuration: boolean;
    };
    inbox: {
      requestDrawers: {
        [key in RequestDrawers]: boolean;
      };
    };
    features: {
      instructionCreation: boolean;
    };
  };
  customer?: {
    // Freight services offered for customer quote creation, keyed by account type, with a `default`
    // for account types not listed. Falls back to the built-in per-account-type defaults in
    // getFreightServices (IATA/IAC -> [AFR], UNCERTIFIED -> [EXW, FCA], default -> all) when omitted.
    freightServices?: Partial<Record<AccountType, FreightService[]>> & {
      default?: FreightService[];
    };
    // Copy for the Shipment Importer box on the customer quote form. Each field falls back to its built-in default when omitted.
    shipmentImporter?: {
      title?: string;
      placeholder?: string;
    };
    // Default checked-state of the HAWB ("consolidated") checkbox on the uncertified quote form. Falls back to the app-wide default when omitted.
    defaultConsolidated?: boolean;
    // Freight service add-ons pre-selected by default on the uncertified quote form (e.g. ['AES']). Falls back to none when omitted.
    defaultFreightServiceAddOns?: FreightServiceAddOn[];
    // Whether the Front live-chat support entry points are shown. Defaults to false (hidden) when omitted.
    supportChat?: boolean;
  };
  termsOfService: string;
  helpUrl?: string;
};
